import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Loader, LoaderSizes } from '@components/Loader/Loader';
import { translate } from '@utils/i18n';
import NoteForm from './components/NoteForm';
import { IndexNamespace, FiltererOptions, SorterOptions } from './constants';
import Activity from './components/Activity';
import FilterSection from './components/FilterSection';
import './ActivityFeed.scss';
import useActivities from './hooks/useActivities';
import useInfiniteScroll from './hooks/useInfiniteScroll';
import useVisibilityListener from './hooks/useVisibilityListener';
import useActivityFeedScroll from './hooks/useActivityFeedScroll';
import { ActivityFeedContext } from './hooks/useActivityFeedCtx';
import useFilters from './hooks/useFilters';
function ActivityFeed({ buildingId, inspectionId, invoiceId, workOrderId, deficiencyId, proposalId, notesEnabled = true, notesVisibilityEnabled = true, compactLayout = false }) {
    const { groupedActivities, isLoading, hasMore, fetchMore } = useActivities({
        buildingId,
        inspectionId,
        invoiceId,
        workOrderId,
        deficiencyId,
        proposalId
    });
    const { data: filtersData } = useFilters();
    const listContainer = useRef(null);
    // The list is only rendered once activities arrive, so a ref alone is still empty when the
    // scroll listener is attached. Tracking the node in state re-runs that effect when it appears.
    const [listNode, setListNode] = useState(null);
    const setListContainer = useCallback((node) => {
        listContainer.current = node;
        setListNode(node);
    }, []);
    useInfiniteScroll(listNode, fetchMore, hasMore, isLoading, (filtersData === null || filtersData === void 0 ? void 0 : filtersData.sort) === SorterOptions.OldestToNewest ? 'up' : 'down');
    const { scrollToBottom, scrollToTop } = useActivityFeedScroll(listContainer);
    const isBuilding = useMemo(() => !(inspectionId || workOrderId || deficiencyId || proposalId || invoiceId), []);
    // Use the scroll hook for visibility listener
    useVisibilityListener(listContainer, (filtersData === null || filtersData === void 0 ? void 0 : filtersData.sort) === SorterOptions.OldestToNewest ? scrollToBottom : scrollToTop);
    const contextValue = useMemo(() => ({
        scrollToBottom,
        scrollToTop
    }), [scrollToBottom, scrollToTop]);
    const containerClass = `workspace__activity activityFeedContainer${compactLayout ? ' activityFeedContainer--compact' : ''}`;
    const filterSection = (React.createElement("div", { className: "activity__minor" },
        React.createElement(FilterSection, { notesEnabled: notesEnabled, horizontal: compactLayout, availableFilters: isBuilding ? [FiltererOptions.System, FiltererOptions.Notes] : Object.values(FiltererOptions) })));
    return (React.createElement(ActivityFeedContext.Provider, { value: contextValue },
        React.createElement("div", { className: containerClass },
            compactLayout && filterSection,
            React.createElement("div", { className: "activity__major" },
                groupedActivities.length === 0 && (React.createElement("section", { className: "workspace__section" }, isLoading ? (React.createElement("div", { className: "loadingContainer" },
                    React.createElement(Loader, { size: LoaderSizes.large }))) : (React.createElement("article", { className: "section__content" }, translate('no_activities', { namespace: IndexNamespace }))))),
                groupedActivities.length > 0 && (React.createElement("div", { className: "activityListContainer", ref: setListContainer },
                    isLoading && (filtersData === null || filtersData === void 0 ? void 0 : filtersData.sort) === SorterOptions.OldestToNewest && React.createElement(Loader, { size: LoaderSizes.large }),
                    groupedActivities.map(({ date, activities }) => (React.createElement("section", { key: date, className: "workspace__section activityGroup" },
                        React.createElement("header", { className: "dateSeparator" },
                            React.createElement("h2", { className: "section__label" }, date),
                            React.createElement("span", null,
                                activities.length,
                                " ",
                                translate('updates', { namespace: IndexNamespace })),
                            React.createElement("hr", { className: "section__split" })),
                        React.createElement("div", { role: "presentation", className: "activity__list" }, activities.map((activity) => (React.createElement(Activity, { activity: activity, isBuilding: isBuilding, key: activity.id }))))))),
                    isLoading && (filtersData === null || filtersData === void 0 ? void 0 : filtersData.sort) === SorterOptions.NewestToOldest && React.createElement(Loader, { size: LoaderSizes.large }))),
                notesEnabled && (React.createElement(NoteForm, { buildingId: buildingId, workOrderId: workOrderId, inspectionId: inspectionId, deficiencyId: deficiencyId, proposalId: proposalId, notesVisibilityEnabled: notesVisibilityEnabled }))),
            !compactLayout && filterSection)));
}
export default ActivityFeed;
